import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { Center, Stack, Text } from "@seneca/senekit";

import { UserAccountType } from "common/types/user";

import {
  getSignInViaEmailLinkLocalStorageDataByField,
  removeSignInViaEmailLinkLocalStorageFields
} from "services/firebase/auth";

import useEffectOnMount from "seneca-common/utils/hooks/useEffectOnMount";

import LoadingSpinner from "features/common/spinners/LoadingSpinner";
import LogoPageWrapper from "features/sign-up/components/LogoPageWrapper";
import { INVALID_EMAIL_ERROR_MESSAGE } from "features/user/actions/auth/passwordless/errorTranslator";
import signInWithEmailLink from "features/user/actions/auth/passwordless/signInWithEmailLink";

import { ConfirmEmailForm } from "./ConfirmEmailForm";
import { PasswordlessSignInCard, PasswordlessSignInWrapper } from "./Layout";

export default function SignInViaLink({
  userAccountType,
  onSignInSuccess,
  onSignUpSuccess,
  onError
}: {
  userAccountType?: UserAccountType;
  onSignInSuccess: () => void;
  onSignUpSuccess: () => void;
  onError: (message: string) => void;
}) {
  const dispatch = useDispatch();

  const emailFromLocalStorage =
    getSignInViaEmailLinkLocalStorageDataByField("email");

  const [isLoading, setIsLoading] = useState(!!emailFromLocalStorage);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const onSignInError = (errorMessage: string) => {
    if (errorMessage === INVALID_EMAIL_ERROR_MESSAGE) {
      setIsLoading(false);
      setErrorMessage(errorMessage);
    } else {
      removeSignInData();
      onError(errorMessage);
    }
  };

  const onSignUp = useCallback(() => {
    removeSignInData();

    onSignUpSuccess();
  }, [onSignUpSuccess]);

  const onSignIn = useCallback(() => {
    removeSignInData();

    onSignInSuccess();
  }, [onSignInSuccess]);

  const signIn = (email: string) => {
    dispatch(
      signInWithEmailLink({
        email,
        windowLocationHref: window.location.href,
        accountType: userAccountType,
        onSignUpSuccess: onSignUp,
        onSignInSuccess: onSignIn,
        onError: onSignInError
      })
    );
  };

  useEffectOnMount(() => {
    if (emailFromLocalStorage) {
      signIn(emailFromLocalStorage);
    }
  });

  return (
    <LogoPageWrapper>
      <PasswordlessSignInWrapper>
        <PasswordlessSignInCard>
          {isLoading ? (
            <Center>
              <Stack space="xxlarge">
                <LoadingSpinner />
                <Text>Almost there! We’re just loading your account...</Text>
              </Stack>
            </Center>
          ) : (
            <ConfirmEmailForm
              errorMessage={errorMessage}
              onSubmit={email => {
                signIn(email);
                setIsLoading(true);
              }}
            />
          )}
        </PasswordlessSignInCard>
      </PasswordlessSignInWrapper>
    </LogoPageWrapper>
  );
}

function removeSignInData() {
  removeSignInViaEmailLinkLocalStorageFields("email", "signInStarted");
}
